import { oktaAuth } from '@/auth';
import { UserClaims } from '@okta/okta-auth-js';
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import * as constants from '../services/constants';
import { setUserId } from '@snowplow/browser-tracker';

export interface IUserContext {
  user?: UserClaims;
  loading: boolean;
  error: Error | null;
}

export const UserContext = createContext<IUserContext | null>(null);

export const UserProvider: FC<PropsWithChildren> = ({ children }) => {
  const [oktaUser, setOktaUser] = useState<UserClaims>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const token = oktaAuth.getAccessToken();

  useEffect(() => {
    const authCheck = async () => {
      // Dev-only bypass (VITE_MOCK_AUTH=true): render the app with a stub user
      // and no Okta/backend, so the mock PhotoVogue UI can be viewed offline.
      // Defaults off; never enable outside local development.
      if (import.meta.env.VITE_MOCK_AUTH === 'true') {
        const mockUser = {
          sub: 'mock-user',
          email: 'dev@condenast.com',
          name: 'PhotoVogue Dev',
        } as UserClaims;
        setOktaUser(mockUser);
        setUserId(mockUser.email);
        sessionStorage.setItem(constants.TOKEN_STORAGE_KEY, 'mock-token');
        setLoading(false);
        return;
      }
      const isAuth = await oktaAuth.isAuthenticated({
        onExpiredToken: 'renew',
      });
      if (!isAuth && !window.location.href.includes('/auth/callback')) {
        await oktaAuth.signInWithRedirect();
        setLoading(false);
      } else {
        const user = await oktaAuth.getUser();
        setOktaUser(user);
        setUserId(user.email);
        if (token) {
          sessionStorage.setItem(constants.TOKEN_STORAGE_KEY, token);
        }
      }
      setLoading(false);
    };
    authCheck().catch(setError);
  }, [oktaAuth, token]);
  return (
    <UserContext.Provider value={{ user: oktaUser, loading, error }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const user = useContext(UserContext);
  if (!user) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return user;
};
